// Backend maintenance detection.
//
// While the backend is in maintenance it answers EVERY request with HTTP 503
// and the JSON body {"code": "maintenance", ...}. Any other 503 (an ingress
// hiccup, an overloaded worker) is an ordinary error and keeps its usual
// handling. The state below only ever turns ON: it is turned off by reloading
// the page once the backend's maintenance/ endpoint reports the window is over
// (see components/Common/MaintenancePage.js).

export const MAINTENANCE_CODE = 'maintenance';
export const MAINTENANCE_STATUS_PATH = 'maintenance/';

export const isMaintenancePayload = (status, data) =>
    status === 503 && !!data && typeof data === 'object' && data.code === MAINTENANCE_CODE;

// True only for an axios error carrying a 503 with code "maintenance".
export const isMaintenanceResponse = (error) =>
    !!error && !!error.response && isMaintenancePayload(error.response.status, error.response.data);

// The maintenance/ endpoint answers 200 {"maintenance": true|false}; only an
// explicit false ends the window, only an explicit true starts it.
export const isMaintenanceOver = (data) => !!data && data.maintenance === false;
export const isMaintenanceOn = (data) => !!data && data.maintenance === true;

let active = false;
const listeners = new Set();

export const isMaintenanceActive = () => active;

export const activateMaintenance = () => {
    if (active)
        return;
    active = true;
    listeners.forEach(listener => listener(true));
};

export const subscribeMaintenance = (listener) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
};

// The DigitalOcean edge replaces the backend's maintenance 503 with its own
// 504 HTML page (the original status survives only in an x-do-orig-status
// header the browser cannot read). So a gateway-type error, or a request that
// got no answer at all, asks the status endpoint instead; the edge passes that
// endpoint's 200 through untouched.
const GATEWAY_STATUSES = [502, 503, 504];
const STATUS_CHECK_INTERVAL_MS = 15000;

export const shouldCheckMaintenanceStatus = (error) =>
    !!error && (!error.response || GATEWAY_STATUSES.includes(error.response.status));

// MaintenancePage registers how to ask the backend (it owns the URL and the
// plain axios call); until then gateway errors are just errors.
let statusFetcher = null;
let lastStatusCheckAt = null;

export const registerMaintenanceStatusFetcher = (fetcher) => {
    statusFetcher = fetcher;
};

const checkMaintenanceStatus = () => {
    const now = Date.now();
    if (!statusFetcher || active
        || (lastStatusCheckAt !== null && now - lastStatusCheckAt < STATUS_CHECK_INTERVAL_MS))
        return;
    lastStatusCheckAt = now;
    statusFetcher()
        .then(res => {
            if (isMaintenanceOn(res.data))
                activateMaintenance();
        }, () => {});
};

const flagMaintenance = (error) => {
    if (isMaintenanceResponse(error)) {
        activateMaintenance();
    } else if (shouldCheckMaintenanceStatus(error)) {
        checkMaintenanceStatus();
    }
    if (error && error.response && error.response.status === 503
        && typeof Blob !== 'undefined' && error.response.data instanceof Blob) {
        // Exports request responseType 'blob', so the JSON body arrives as a
        // Blob. Reading it does not consume it, so ErrorHandler can still read
        // it afterwards.
        error.response.data.text()
            .then(text => {
                if (isMaintenancePayload(503, JSON.parse(text)))
                    activateMaintenance();
            })
            .catch(() => {});
    }
    // Callers still see the rejection exactly as before.
    return Promise.reject(error);
};

// Attaches maintenance detection to an axios instance and returns it.
export const withMaintenanceDetection = (instance) => {
    instance.interceptors.response.use(undefined, flagMaintenance);
    return instance;
};
